// src/pages/SortiesPhoto.jsx
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import Navigation from "../components/Navigation";
import SEO from "../components/SEO";

import HeroBgImg from "../assets/images/pictures_gallery/exterieur.webp";

// ---------------------------------------------------------------------------
// Motion variants
// ---------------------------------------------------------------------------
const heroContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const heroItemVariants = {
  hidden: { y: 30 },
  visible: {
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const listContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const rowItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

const fadeInVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

// ---------------------------------------------------------------------------
// Section heading — label + hairline + h2
// ---------------------------------------------------------------------------
const SectionHeading = ({ label, title, id }) => (
  <div className="sp-section-heading">
    <span className="sp-section-label" aria-hidden="true">{label}</span>
    <span className="sp-section-rule" aria-hidden="true" />
    <h2 id={id} className="sp-section-title">{title}</h2>
  </div>
);

// ---------------------------------------------------------------------------
// ParcoursRow — numbered editorial list item
// ---------------------------------------------------------------------------
const ParcoursRow = ({
  index,
  title,
  badge,
  description,
  concepts,
  priceClassic,
  pricePremium,
  note,
  premiumDetail,
}) => {
  const shouldReduce = useReducedMotion();
  const num = String(index).padStart(2, "0");

  return (
    <motion.li
      className="parcours-row"
      variants={shouldReduce ? {} : rowItemVariants}
    >
      <span className="parcours-row__number" aria-hidden="true">{num}</span>
      <motion.div
        className="parcours-row__content"
        whileHover={shouldReduce ? {} : { x: 8 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
      >
        <div className="parcours-row__header">
          <h3 className="parcours-row__title">{title}</h3>
          <span className="parcours-row__badge">{badge}</span>
        </div>
        {note && (
          <p className="parcours-row__note">
            <span aria-hidden="true">— </span>{note}
          </p>
        )}
        <p className="parcours-row__description">{description}</p>
        <ul
          className="parcours-row__concepts"
          aria-label="Concepts abordés"
        >
          {concepts.map((c, i) => (
            <li key={i} className="concept-chip">{c}</li>
          ))}
        </ul>
        {premiumDetail && (
          <p className="parcours-row__premium-detail">
            <span aria-hidden="true">Premium — </span>{premiumDetail}
          </p>
        )}
      </motion.div>
      <div
        className="parcours-row__price"
        aria-label={`Tarif : ${priceClassic} euros classique, ${pricePremium} euros premium`}
      >
        <span className="price-block price-block--classic">
          <span className="price-block__amount">{priceClassic}€</span>
          <span className="price-block__label">classique</span>
        </span>
        <span className="price-block__sep" aria-hidden="true">/</span>
        <span className="price-block price-block--premium">
          <span className="price-block__amount">{pricePremium}€</span>
          <span className="price-block__label">premium</span>
        </span>
      </div>
    </motion.li>
  );
};

// ---------------------------------------------------------------------------
// PassItem — horizontal pass card
// ---------------------------------------------------------------------------
const PassItem = ({ name, contents, priceClassic, pricePremium }) => (
  <div className="pass-item">
    <h3 className="pass-item__name">{name}</h3>
    <p className="pass-item__contents">{contents}</p>
    <div className="pass-item__prices">
      <span className="pass-item__price pass-item__price--classic">
        <span className="pass-item__amount">{priceClassic}€</span>
        <span className="pass-item__label">classique</span>
      </span>
      <span className="pass-item__price pass-item__price--premium">
        <span className="pass-item__amount">{pricePremium}€</span>
        <span className="pass-item__label">premium</span>
      </span>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const SortiesPhoto = () => {
  const shouldReduce = useReducedMotion();

  const heroAnim = shouldReduce
    ? { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 } }
    : { initial: "hidden", animate: "visible" };

  const inViewAnim = shouldReduce
    ? { initial: false, whileInView: false }
    : { initial: "hidden", whileInView: "visible" };

  return (
    <main className="sp-container">
      <SEO
        title="Sorties Photo Lyon"
        path="/sorties-photo"
        description="Sorties photo à Lyon en petit groupe : concert, portrait, street art, golden hour. Apprenez à photographier sur le terrain avec Alexandre Jack. Dès 35 €."
      />
      <Navigation />

      {/* ── HERO — full-bleed photo background ─────────────────────────────── */}
      <section className="sp-hero" aria-labelledby="sp-hero-heading">
        <div className="sp-hero__bg" aria-hidden="true">
          <img src={HeroBgImg} alt="" loading="eager" decoding="async" />
        </div>
        <div className="sp-hero__overlay" aria-hidden="true" />

        <motion.div
          className="sp-hero__content"
          variants={shouldReduce ? {} : heroContainerVariants}
          {...heroAnim}
        >
          <motion.p
            className="sp-hero__eyebrow"
            variants={shouldReduce ? {} : heroItemVariants}
          >
            Alexandre Jack · Lyon
          </motion.p>

          <motion.h1
            id="sp-hero-heading"
            className="sp-hero__title"
            variants={shouldReduce ? {} : heroItemVariants}
          >
            Sorties Photo
          </motion.h1>

          <motion.p
            className="sp-hero__subtitle"
            variants={shouldReduce ? {} : heroItemVariants}
          >
            &amp; Ateliers de photographie
          </motion.p>

          <motion.ul
            className="sp-hero__stats"
            aria-label="Informations clés"
            variants={shouldReduce ? {} : heroItemVariants}
          >
            <li>6 participants max</li>
            <li>Coaching sur mesure</li>
            <li>Lyon &amp; environs</li>
          </motion.ul>
        </motion.div>
      </section>

      <div className="sp-body">

        {/* ── SORTIES COURTES 2H ─────────────────────────────────────────── */}
        <section aria-labelledby="heading-courtes">
          <SectionHeading label="Durée · 2 heures" title="Sorties courtes" id="heading-courtes" />
          <motion.ol
            className="parcours-list"
            aria-label="Parcours disponibles"
            variants={shouldReduce ? {} : listContainerVariants}
            {...inViewAnim}
            viewport={{ once: true, margin: "-80px" }}
          >
            <ParcoursRow
              index={1}
              title="Vieux Lyon & quais de Saône"
              badge="Vieux Lyon"
              description="Traboules, ruelles médiévales, pont Bonaparte. Jeux d'ombre, de matière et reflets sur la Saône."
              concepts={["Contrastes ombre/lumière", "Cadrage en espace restreint"]}
              priceClassic={35}
              pricePremium={55}
            />
            <ParcoursRow
              index={2}
              title="Pentes de la Croix-Rousse"
              badge="Croix-Rousse"
              description="Fresques murales, rue Leynaud, jardin des Chartreux. Street art et instant décisif."
              concepts={["Street art", "Portraits spontanés"]}
              priceClassic={35}
              pricePremium={55}
            />
            <ParcoursRow
              index={3}
              title="Villeurbanne – Gratte-Ciel"
              badge="Art déco"
              description="Avenue Henri Barbusse, mairie, place Lazare Goujon. Symétrie et architecture Art déco."
              concepts={["Symétrie", "Détails architecturaux"]}
              note="Les cours intérieures sont privées — nous photographions l'extérieur."
              priceClassic={35}
              pricePremium={55}
            />
          </motion.ol>
        </section>

        {/* ── DEMI-JOURNÉES 4H ───────────────────────────────────────────── */}
        <section aria-labelledby="heading-demijournees">
          <SectionHeading label="Durée · 4 heures" title="Demi-journées" id="heading-demijournees" />
          <motion.ol
            className="parcours-list"
            aria-label="Parcours disponibles"
            variants={shouldReduce ? {} : listContainerVariants}
            {...inViewAnim}
            viewport={{ once: true, margin: "-80px" }}
          >
            <ParcoursRow
              index={1}
              title="Presqu'île → Confluence"
              badge="Contraste"
              description="De Bellecour au musée des Confluences. Haussmannien vs. futuriste, reflets et transparences."
              concepts={["Contraste ancien/moderne", "Reflets", "Minimalisme"]}
              priceClassic={65}
              pricePremium={95}
            />
            <ParcoursRow
              index={2}
              title="Parc de la Tête d'Or & berges"
              badge="Nature urbaine"
              description="Serres, lac, roseraie, berges du Rhône. Macro, mouvement, lumière naturelle."
              concepts={["Photo de nature", "Capture du mouvement", "Macro"]}
              priceClassic={65}
              pricePremium={95}
            />
            <ParcoursRow
              index={3}
              title="Fourvière → Croix-Rousse"
              badge="Panoramas"
              description="Funiculaire, basilique, jardin du Rosaire, montée de la Grande Côte. Lumières changeantes et vues à 360°."
              concepts={["Panoramique", "Gestion de la lumière", "Narration visuelle"]}
              priceClassic={65}
              pricePremium={95}
            />
          </motion.ol>
        </section>

        {/* ── ATELIERS THÉMATIQUES ───────────────────────────────────────── */}
        <section aria-labelledby="heading-ateliers">
          <SectionHeading label="Durée · 2 heures" title="Ateliers thématiques" id="heading-ateliers" />
          <motion.ol
            className="parcours-list"
            aria-label="Parcours disponibles"
            variants={shouldReduce ? {} : listContainerVariants}
            {...inViewAnim}
            viewport={{ once: true, margin: "-80px" }}
          >
            <ParcoursRow
              index={1}
              title="Initiation argentique"
              badge="Pellicule 35 mm"
              description="Mise au point manuelle, prévisualisation, choix de la pellicule. Prêt de boîtier possible."
              concepts={["Lenteur créative", "Triangle d'exposition fixe"]}
              priceClassic={45}
              pricePremium={75}
              premiumDetail="pellicule offerte + serveur Discord"
            />
            <ParcoursRow
              index={2}
              title="Photo nocturne"
              badge="Nuit"
              description="Pose longue, files de lumière, reflets sur la Saône. Trépied obligatoire."
              concepts={["Pose longue", "Gestion du bruit"]}
              priceClassic={45}
              pricePremium={75}
              premiumDetail="évaluation personnalisée + serveur Discord"
            />
          </motion.ol>
        </section>

        {/* ── FORMULES & PASS ────────────────────────────────────────────── */}
        <section aria-labelledby="heading-pass">
          <SectionHeading label="Tarification" title="Formules & Pass" id="heading-pass" />
          <motion.div
            className="pass-grid"
            variants={shouldReduce ? {} : listContainerVariants}
            {...inViewAnim}
            viewport={{ once: true, margin: "-60px" }}
          >
            <PassItem
              name="Découverte"
              contents="3 sorties courtes (2h)"
              priceClassic="95"
              pricePremium="155"
            />
            <PassItem
              name="Exploration"
              contents="2 demi-journées (4h)"
              priceClassic="120"
              pricePremium="180"
            />
            <PassItem
              name="Argentique"
              contents="1 atelier argentique + 2 sorties courtes"
              priceClassic="110"
              pricePremium="175"
            />
            <PassItem
              name="Intensif"
              contents="3 sorties courtes + 3 demi-journées"
              priceClassic="285"
              pricePremium="435"
            />
          </motion.div>
        </section>

        {/* ── INCLUS DANS CHAQUE SORTIE ──────────────────────────────────── */}
        <section aria-labelledby="heading-inclus">
          <SectionHeading label="Ce qui est compris" title="Inclus dans chaque sortie" id="heading-inclus" />
          <motion.div
            className="inclus-wrapper"
            variants={shouldReduce ? {} : fadeInVariants}
            {...inViewAnim}
            viewport={{ once: true, margin: "-60px" }}
          >
            <ul className="inclus-list">
              <li>Fiche technique du lieu (PDF)</li>
              <li>Check-list matériel personnalisée</li>
              <li>Livret « Défis Photos » pendant la balade</li>
              <li>Fiche post-traitement après la sortie</li>
              <li>Bon de réduction —10 % pour la prochaine sortie</li>
            </ul>
            <aside className="premium-aside" aria-label="Avantages premium">
              <span className="premium-aside__label">Premium</span>
              <p>
                Évaluation personnalisée des photos — accès au serveur Discord
                privé — pellicule offerte pour l'atelier argentique.
              </p>
            </aside>
          </motion.div>
        </section>

        {/* ── RÉSERVATION ────────────────────────────────────────────────── */}
        <section aria-labelledby="heading-booking" className="sp-section-booking">
          <motion.div
            className="booking-block"
            variants={shouldReduce ? {} : fadeInVariants}
            {...inViewAnim}
            viewport={{ once: true, margin: "-60px" }}
          >
            <h2 id="heading-booking" className="booking-block__headline">Réservez</h2>
            <p className="booking-block__copy">
              Sans création de compte — choisissez une date et remplissez vos coordonnées.
              Le lien s'ouvre dans Cal.com.
            </p>
            <a
              href="https://cal.com/ton-username"
              target="_blank"
              rel="noopener noreferrer"
              className="booking-btn"
              aria-label="Réserver une sortie ou un atelier sur Cal.com — ouvre un nouvel onglet"
            >
              Choisir une date
            </a>
          </motion.div>
        </section>

      </div>
    </main>
  );
};

export default SortiesPhoto;
